<script setup lang="ts">
import { ref, computed } from "vue";

import { v4 as uuidv4 } from "uuid";

import { themeProp, useTheme } from "@/features/Themes";

import { InputChangeType } from "../types/InputChangeType";

import { checkboxProp } from "../props/inputProps";

const props = defineProps({
  ...themeProp,
  ...checkboxProp,
  showToggle: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits<{
  (e: "change", payload: InputChangeType): void;
  (e: "toggleVisibility", payload: any): void;
}>();

const checkboxId = ref(uuidv4());

const onChange = (event: Event) => {
  if (props.disabled) return;
  const target = event.target as HTMLInputElement;
  emits("change", { name: props.name, value: target.checked });
};

const theme = computed(() =>
  useTheme().get({
    config: {
      appBlue: {
        passive: {
          bgLabel: props.hover ? "hover:bg-app-blue-50" : "",
          bg: "bg-app-blue-50",
          border: "border-app-blue-600",
          icon: "text-app-blue-600",
        },
      },
      slate: {
        passive: {
          bgLabel: props.hover ? "hover:bg-slate-50" : "",
          bg: "bg-slate-0",
          border: "border-slate-600",
          icon: "text-slate-600",
        },
      },
    },
    theme: props.theme,
  })
);
</script>

<template>
  <label
    :for="checkboxId"
    class="t-small rounded-4 p-px-8 block bg-transparent transition-all"
    :class="[theme.bgLabel, disabled ? 'cursor-default opacity-60' : 'cursor-pointer']">
    <input
      :id="checkboxId"
      type="checkbox"
      :disabled="disabled"
      :checked="checked === true"
      class="hidden"
      @click="onChange" />

    <div class="gap-px-8 flex items-center">
      <div
        class="h-px-16 w-px-16 rounded-4 flex shrink-0 items-center justify-center border shadow-xs"
        :class="[theme.bg, theme.border]">
        <font-awesome-icon
          v-if="checked === true"
          icon="fa-solid fa-check"
          :class="[theme.icon, 'text-10']" />
        <font-awesome-icon
          v-else-if="checked === 'indeterminate'"
          icon="fa-solid fa-minus"
          :class="[theme.icon, 'text-10']" />
      </div>

      <span
        v-if="label"
        class="text-type-default whitespace-nowrap">
        {{ label }}
      </span>
    </div>
  </label>
</template>
